import React, { FC, forwardRef, HTMLAttributes, Ref, useContext, useEffect, useState } from 'react';
import { pdpSectionStyle } from 'common/components/page-section';
import { checkIfReviewsAreLoaded } from 'features/reviews/selectors';
import { Adaptive } from 'fronton-react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { RootReducer } from 'store/root-reducer';
import { useTranslation } from 'react-i18next';
import {
  LARGE_DESK_SCREEN,
  MEDIUM_DESK_SCREEN,
  TABLET_SCREEN,
  MOBILE_SCREEN,
} from 'utils/internal-specifics/media-queries';
import { addMediaQueryHandlers } from 'utils/add-media-query-handlers';
import { Context } from 'features/page-navigation/components/section-refs-context';
import { AntifadeContainer } from 'common/components/section-vlimit/fade-container/fade-container';
import { SectionHeader } from 'common/components';
import { Breakpoint } from 'types/utils';
import { useInView } from 'react-cool-inview';
import { cx } from 'linaria';
import { pdpSectionInsideInfoBlockStyle } from 'pages/product-details/styles';
import { additionalTitleMargin, reviewsSectionContainer } from './styles';
import { PdpReviewsPhotosModal } from '../pdp-reviews-photos-modal/pdp-reviews-photos-modal';
import { PDP_PRODUCT_REVIEWS_IN_VIEWPORT } from '../../actions';
import { ReviewsSectionFull, ReviewsSectionLite } from './content-blocks';

type ProductReviewsMappedState = {
  reviewsAreLoaded: boolean;
};

type ProductReviewsProps = ProductReviewsMappedState & HTMLAttributes<HTMLElement>;

export const DATA_QA_REVIEWS = 'product-reviews';

const ProductReviews = forwardRef<HTMLElement, ProductReviewsProps>(
  ({ reviewsAreLoaded, className = '', ...htmlProps }, ref) => {
    const [breakpoint, setBreakpoint] = useState<Breakpoint>('large');
    const { t } = useTranslation();
    useEffect(
      () =>
        addMediaQueryHandlers(
          [LARGE_DESK_SCREEN, MEDIUM_DESK_SCREEN, TABLET_SCREEN, MOBILE_SCREEN],
          ['large', 'medium', 'tablet', 'mobile'],
          'large',
          setBreakpoint,
        ),
      [],
    );
    const navigationRef = useContext(Context).get('reviews');

    return (
      <section
        ref={ref}
        className={cx(className, pdpSectionStyle, reviewsSectionContainer, pdpSectionInsideInfoBlockStyle)}
        {...htmlProps}
        data-qa={DATA_QA_REVIEWS}
      >
        <Adaptive from="tablet">
          <AntifadeContainer />
        </Adaptive>
        <div ref={navigationRef as Ref<HTMLDivElement>}>
          <SectionHeader dataQA="reviews-section-title_mf-pdp" className={additionalTitleMargin}>
            {t('reviews.ui.title')}
          </SectionHeader>
          {reviewsAreLoaded ? <ReviewsSectionFull breakpoint={breakpoint} /> : <ReviewsSectionLite />}
        </div>
        <PdpReviewsPhotosModal breakpoint={breakpoint} />
      </section>
    );
  },
);

/**
 * @internal
 */
export const withViewportObserver =
  (
    Component: FC<ProductReviewsProps & { ref: Ref<HTMLElement> }>,
  ): FC<
    ProductReviewsProps & {
      viewportPositionChanged: (params: { viewportPosition: 'in-viewport' | 'out-of-viewport' }) => void;
    }
  > =>
  ({ viewportPositionChanged, ...props }) => {
    const { observe, inView } = useInView({ rootMargin: '0px 0px -10% 0px', unobserveOnEnter: true });

    useEffect(() => {
      viewportPositionChanged({ viewportPosition: inView ? 'in-viewport' : 'out-of-viewport' });
    }, [inView, viewportPositionChanged]);

    return <Component ref={observe} {...props} />;
  };

const mapStoreToProps = (store: RootReducer): ProductReviewsMappedState => ({
  reviewsAreLoaded: checkIfReviewsAreLoaded(store),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  viewportPositionChanged: ({
    viewportPosition,
  }: {
    viewportPosition: 'in-viewport' | 'out-of-viewport';
  }) => {
    if (viewportPosition === 'in-viewport') {
      dispatch(PDP_PRODUCT_REVIEWS_IN_VIEWPORT());
    }
  },
});

export const PdpProductReviews = connect(
  mapStoreToProps,
  mapDispatchToProps,
)(withViewportObserver(ProductReviews));
